import React, { useState } from 'react';
import {
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import BookIcon from '@mui/icons-material/Book';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getAdministrationMenuItems,
  getAuthenticatedHomePath,
  getEinsatzMenuItems,
  getGrundlehrgangSrhtMenuItems,
  getMenuPermissions,
  getRueckmeldungenMenuItems,
} from '../config/roleAccess';

function DrawerMenu({ open, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = getMenuPermissions(user);
  const rueckmeldungenMenuItems = getRueckmeldungenMenuItems(user);
  const administrationMenuItems = getAdministrationMenuItems(user);
  const grundlehrgangSrhtMenuItems = getGrundlehrgangSrhtMenuItems(user);
  const einsatzMenuItems = getEinsatzMenuItems(user);

  const [openMenus, setOpenMenus] = useState({
    einsatz: false,
    logbuch: false,
    rueckmeldung: false,
    administration: false,
    grundlehrgangSrht: false,
  });

  const openOnlyMenu = (menu) => {
    setOpenMenus((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        next[key] = key === menu ? !prev[key] : false;
      });
      return next;
    });
  };

  const go = (path) => {
    onClose();
    navigate(path);
  };

  const disabledNestedItemSx = {
    pl: 4,
    opacity: 0.5,
    '&.Mui-disabled': {
      opacity: 0.5,
    },
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '88vw', sm: 320 }, maxWidth: 360 } }}
    >
      <List>
        <ListItem disablePadding>
          <ListItemButton sx={{ minHeight: 46 }} onClick={() => go(getAuthenticatedHomePath(user))}>
            <ListItemIcon><HomeIcon /></ListItemIcon>
            <ListItemText primary="Startseite" />
          </ListItemButton>
        </ListItem>
        <Divider />

        {permissions.showEinsatz && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => openOnlyMenu('einsatz')}>
                <ListItemIcon><BookIcon /></ListItemIcon>
                <ListItemText primary="Einsatz" />
                {openMenus.einsatz ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openMenus.einsatz} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {einsatzMenuItems.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => go(item.path)}>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {permissions.showLogbuch && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => openOnlyMenu('logbuch')}>
                <ListItemIcon><BookIcon /></ListItemIcon>
                <ListItemText primary="Logbuch" />
                {openMenus.logbuch ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openMenus.logbuch} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {permissions.logbuchEintragVornehmen && (
                  <ListItem disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => go('/eintrag-vornehmen')}>
                      <ListItemText primary="Eintrag vornehmen" />
                    </ListItemButton>
                  </ListItem>
                )}
                {permissions.logbuchAnsicht && (
                  <ListItem disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => go('/logbuch')}>
                      <ListItemText primary="Logbuch" />
                    </ListItemButton>
                  </ListItem>
                )}
                {permissions.logbuchStatistik && (
                  <ListItem disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => go('/statistik')}>
                      <ListItemText primary="Statistik" />
                    </ListItemButton>
                  </ListItem>
                )}
                {permissions.logbuchUebungsplanung && (
                  <ListItem disablePadding>
                    <ListItemButton disabled sx={disabledNestedItemSx}>
                      <ListItemText primary="Übungsplanung" />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {permissions.showRueckmeldungen && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => openOnlyMenu('rueckmeldung')}>
                <ListItemIcon><FeedbackIcon /></ListItemIcon>
                <ListItemText primary="Rückmeldungen" />
                {openMenus.rueckmeldung ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openMenus.rueckmeldung} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {rueckmeldungenMenuItems.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => go(item.path)}>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {grundlehrgangSrhtMenuItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => openOnlyMenu('grundlehrgangSrht')}>
                <ListItemIcon><SchoolIcon /></ListItemIcon>
                <ListItemText primary="Grundlehrgang SRHT" />
                {openMenus.grundlehrgangSrht ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openMenus.grundlehrgangSrht} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {grundlehrgangSrhtMenuItems.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={item.disabled ? undefined : () => go(item.path)}
                      disabled={Boolean(item.disabled)}
                    >
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {administrationMenuItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => openOnlyMenu('administration')}>
                <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                <ListItemText primary="Administration" />
                {openMenus.administration ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openMenus.administration} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {administrationMenuItems.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => go(item.path)}>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Drawer>
  );
}

export default DrawerMenu;
